import Button, { KINDS, SIZE } from "@/components/Button";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_CHAIN,
  getWalletConfigChainId,
  isSupportChainId,
} from "@/libs/PrivyProvider";
import { shortenAddress } from "@/libs/Address";
import { useAccount } from "@/hooks/useAccount";
import { useTokenBalances } from "@/hooks/useTokenBalances";
import BigNumber from "bignumber.js";
import { NATIVE_ADDRESS } from "@/constants/addresses";
import { NATIVE } from "@/constants/tokens";
import { GET } from "@/libs/Networks";
import OpenChainModal from "../OpenChainModal";
import { toTokenFormattedAmount } from "@/libs/Number";
import UserModal from "../UserModal";
import clsx from "clsx";
import Modal from "../Modal";
import { toTokenFormattedAmount222 } from "@/libs/Number222";

interface PrivyConnectButtonProps {
  size?: SIZE;
  kind?: KINDS;
  wide?: boolean;
  className?: string;
}
export const PrivyConnectButton = ({
  className,
  kind,
  wide = false,
  ...props
}: PrivyConnectButtonProps) => {
  const [showOpenChain, setShowOpenChain] = useState(false);
  const [showUser, setShowUser] = useState(false);

  const { wallets } = useWallets();
  const { chain, address } = useAccount();
  const { tokenBalances } = useTokenBalances();
  const native = useMemo(() => NATIVE[GET(chain?.id)], [chain?.id]);

  const srcTokenBalance = useMemo(() => {
    return toTokenFormattedAmount222(
      tokenBalances[NATIVE_ADDRESS]?.balance ?? BigNumber(0),
      native.decimals,
      { mantissa: 3 }
    );
  }, [native.decimals, tokenBalances]);

  const { ready, authenticated, login, logout } = usePrivy();
  const isSupportChain = isSupportChainId(chain?.id);

  const handleShowUserModal = useCallback(() => {
    setShowUser(true);
  }, []);

  const injectedWallet = useMemo(
    () =>
      wallets.find(
        (wallet, i) =>
          wallet.connectorType === "injected" &&
          wallet.address === address &&
          i < 2
      ),
    [wallets, address]
  );

  const switchNetwork = useCallback(async () => {
    if (injectedWallet) {
      await injectedWallet.switchChain(DEFAULT_CHAIN.id);
    }
  }, [injectedWallet]);

  useEffect(() => {
    if (injectedWallet && chain?.id !== DEFAULT_CHAIN.id) {
      switchNetwork();
    }
  }, [address, chain?.id, injectedWallet, switchNetwork, wallets]);

  const handleLogin = useCallback(async () => {
    if (ready && !authenticated) {
      await login();
    }
  }, [authenticated, login, ready]);

  const walletConfig = useMemo(
    () => getWalletConfigChainId(chain?.id),
    [chain?.id]
  );

  return (
    <>
      {ready ? (
        !address ? (
          <Button
            className={clsx(
              className,
              "!py-0 md:!px-4 xl:!px-8 h-[60px] md:!h-[40px] xl:!h-[60px] text-base md:text-sm lg:text-base"
            )}
            onClick={handleLogin}
            size="medium"
            kind={kind}
            noShadow
            wide={wide}
          >
            Log In
          </Button>
        ) : isSupportChain ? (
          <div className="flex flex-wrap sm:flex-nowrap justify-center gap-6 md:gap-2 xl:gap-4">
            {/* <Button
              size="medium"
              kind="main"
              onClick={() => {
                setShowOpenChain(true);
              }}
            >
              {walletConfig?.iconUrl && (
                <div className="flex items-center justify-center overflow-hidden rounded-full h-7 w-7">
                  {walletConfig?.iconUrl && (
                    <img
                      alt={walletConfig?.name || "Chain icon"}
                      src={walletConfig?.iconUrl}
                      style={{ width: 28, height: 28 }}
                    />
                  )}
                </div>
              )}
            </Button> */}
            <Button
              size="medium"
              kind="black"
              noShadow
              onClick={handleShowUserModal}
              className="!py-0 md:!px-2 xl:!px-8 h-[60px] md:!h-[40px] xl:!h-[60px] text-base md:text-sm lg:text-base"
              // {...props}
            >
              {shortenAddress(address, 2)}{" "}
              {srcTokenBalance ? ` (${srcTokenBalance} ${native.symbol})` : ""}
            </Button>
          </div>
        ) : (
          <Button
            onClick={switchNetwork}
            {...props}
            size="medium"
            kind="black"
            noShadow
            className="!py-0 md:!px-4 xl:!px-8 h-[60px] md:!h-[40px] xl:!h-[60px] text-base md:text-sm lg:text-base"
          >
            {chain.name} Wrong network
          </Button>
        )
      ) : (
        <Button
          className={clsx(
            "animate-pulse pointer-events-none !py-0 md:!px-4 xl:!px-8 h-[60px] md:!h-[40px] xl:!h-[60px] text-base md:text-sm lg:text-base"
          )}
          size="medium"
          kind="black"
          noShadow
          wide={wide}
        >
          ...
        </Button>
      )}

      {showOpenChain && (
        <OpenChainModal
          isOpen={showOpenChain}
          onDismiss={() => {
            setShowOpenChain(false);
          }}
        />
      )}

      {showUser && (
        <Modal
          title={"User Profile"}
          isShow={showUser}
          onClose={() => {
            setShowUser(false);
          }}
          // background="sm:bg-opacity-25 bg-bitzy-white"
          size={"sm"}
        >
          <UserModal
            onDismiss={() => {
              setShowUser(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};
